import type { IssueJobIds, IssueQueue } from "@domain/CRM/job";
import { StandardAxiosConfig, getBaseDataURL } from "../../../common";
import { apiClient, reportPartialServerError } from "../../../client";

/**
 * The ids of the jobs on the ops "issues" queues, for the app shell's count badges: one request for
 * every queue the viewer has a badge for, in place of one full job list per queue (those lists are
 * `JobsAPI.issues`, and still feed the issue pages themselves).
 */
export class JobIssueIdsAPI {
	/**
	 * `GET /jobs/issues/ids?queue=…&queue=…`. Rejects when the request itself fails, as any request does.
	 * A queue the server could not read does not reject: it comes back named in `failed`, with the other
	 * queues answered, and is reported here as the failed request it used to be (a "Server error" toast
	 * and a Sentry report) — the caller only has to leave that queue's count unknown.
	 *
	 * Against an API that has no such route yet (a 404) the same answer is built from the queues' list
	 * routes instead: see `idsFromListRoutes`.
	 */
	async get(queues: readonly IssueQueue[]): Promise<IssueJobIds> {
		const query = new URLSearchParams(queues.map((queue) => ["queue", queue]));
		const url = `${issuesURL()}/ids?${query}`;
		const response = await apiClient.get<IssueJobIds>(url, StandardAxiosConfig()).catch((error: unknown) => {
			if (isRouteMissing(error)) return undefined;
			throw error;
		});
		if (!response) return idsFromListRoutes(queues);
		if (response.status !== 200) throw new Error("Failed to get the issue job ids");
		const { failed } = response.data;
		if (failed.length > 0) {
			reportPartialServerError(new Error(`Issue queues failed to load: ${failed.join(", ")}`));
		}
		return response.data;
	}
}

const issuesURL = () => `${getBaseDataURL("jobs")}/issues`;

/** The API answered 404: it is a build from before `GET /jobs/issues/ids` existed. */
function isRouteMissing(error: unknown): boolean {
	return (error as { response?: { status?: number } } | undefined)?.response?.status === 404;
}

/**
 * ROLLOUT GUARD (customer-portal-tm2x.2) — delete once every environment's API serves
 * `GET /jobs/issues/ids`, together with `isRouteMissing` and the `.catch` in `get`.
 *
 * The Web and the API deploy side by side, and the Web usually lands first; an API deploy can also fail
 * or be rolled back. Against an API without the route, the badges would silently show no counts. So in
 * that case the same answer is built the way the badges were fed before: one `GET /jobs/issues/<queue>`
 * list per queue (a queue is named after its list route), each failing alone.
 *
 * A list request that fails has already been toasted and reported by the interceptor, exactly as it
 * was then, so nothing more is reported here — the queue is only named in `failed`.
 */
async function idsFromListRoutes(queues: readonly IssueQueue[]): Promise<IssueJobIds> {
	const lists = await Promise.allSettled(
		queues.map((queue) => apiClient.get<{ id: string }[]>(`${issuesURL()}/${queue}`, StandardAxiosConfig())),
	);
	const answer: IssueJobIds = { ids: {}, failed: [] };
	lists.forEach((list, i) => {
		if (list.status === "fulfilled" && list.value.status === 200) {
			answer.ids[queues[i]] = list.value.data.map((job) => job.id);
		} else {
			answer.failed.push(queues[i]);
		}
	});
	return answer;
}
